// useHowItLands — the shell side of the "How it lands in …" panel
// (HowItLands.tsx): asks the server how what Export sends out lands in the
// format the project is made for (on open, whenever the library changes,
// and on Try again), and runs the panel's intents on the shell's doors:
// Show me opens the page the text is on and points at it; Move it asks the
// server to move it (the next answer has no proposal). The answer is the
// server's (derived, never stored); what is kept here is view state only:
// whether the panel is open, what is pointed at, which proposal is moving.
// A failed ask keeps Export working (the panel says so; Try again).

export type { HowItLandsPanel };
export { landingFormatOf, useHowItLands };

import { Result } from "@bilda/utils";
import { useEffect, useState } from "react";
import type { StudioCard } from "../../../components/Studio/studioCards.js";
import {
    type DeskCommand,
    openThingCommands,
} from "../../../components/Studio/studioDesk.js";
import type { LandingAnswer } from "../../../components/StudioLanding/landingView.js";
import type { LandingFormat, MoveTextProposal } from "../../ports/landing.js";
import type { ProjectFormat } from "../../ports/project.js";
import type { AppPorts, Library } from "../../ports.js";

// The course format a project is made for, if the panel speaks of it.
const landingFormatOf = (
    format: ProjectFormat | undefined,
): LandingFormat | undefined => {
    if (format?.type !== "course") return undefined;
    switch (format.platform) {
        case "F_torus":
            return "torus";
        case "F_canvas":
            return "canvas";
        default:
            return undefined;
    }
};

type HowItLandsPanel = {
    readonly open: boolean;
    readonly answer: LandingAnswer | undefined;
    readonly pointed: ReadonlySet<string>;
    readonly moving: string | undefined;
    readonly onOpen: () => void;
    readonly onClose: () => void;
    readonly onRetry: () => void;
    readonly onPoint: (ids: readonly string[]) => void;
    readonly onShowMe: (p: MoveTextProposal) => void;
    readonly onMoveIt: (p: MoveTextProposal) => void;
};

const useHowItLands = (
    ports: AppPorts,
    library: Library | null,
    // What Export sends out, and the format (either absent: no panel).
    target: string | undefined,
    format: LandingFormat | undefined,
    cards: readonly StudioCard[],
    onDesk: (...commands: readonly DeskCommand[]) => void,
    // The id of the page shown (to open the text's page only when away).
    shown: string | undefined,
    onMoveFailed: () => void,
): HowItLandsPanel => {
    const [open, setOpen] = useState(false);
    const [answer, setAnswer] = useState<LandingAnswer | undefined>(undefined);
    const [pointed, setPointed] = useState<ReadonlySet<string>>(new Set());
    const [moving, setMoving] = useState<string | undefined>(undefined);
    // Try again, and a move done: ask once more.
    const [asked, setAsked] = useState(0);
    const asks = target !== undefined && format !== undefined;

    // biome-ignore lint/correctness/useExhaustiveDependencies: `asked` IS the re-ask signal; the body deliberately doesn't read it.
    useEffect(() => {
        if (library === null || target === undefined || format === undefined) {
            setAnswer(undefined);
            return;
        }
        let current = true;
        // The last answer stays while the next is on its way (no flicker on
        // a live refresh); the Waiting line shows only when there is none.
        setAnswer((a) => (a?.type === "landed" ? a : { type: "waiting" }));
        const go = async () => {
            const r = await ports.loadHowItLands(target, format);
            if (!current) return;
            setAnswer(
                Result.isOk(r)
                    ? { type: "landed", landing: r.value }
                    : { type: "failed" },
            );
        };
        void go();
        return () => {
            current = false;
        };
    }, [ports, library, target, format, asked]);

    return {
        open: open && asks,
        answer,
        pointed,
        moving,
        onOpen: () => setOpen(true),
        onClose: () => {
            setOpen(false);
            setPointed(new Set());
        },
        onRetry: () => {
            setAnswer({ type: "waiting" });
            setAsked((n) => n + 1);
        },
        onPoint: (ids) => setPointed(new Set(ids)),
        onShowMe: (p) => {
            setOpen(true);
            if (shown !== p.container)
                onDesk(...openThingCommands(cards, p.container));
            setPointed(new Set(p.blocks));
        },
        onMoveIt: (p) => {
            setMoving(p.part);
            const go = async () => {
                const r = await ports.moveTextIntoPart(p);
                setMoving(undefined);
                if (Result.isErr(r)) onMoveFailed();
                setPointed(new Set());
                setAsked((n) => n + 1);
            };
            void go();
        },
    };
};
